import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Ui, Heading3 } from '@silvertours/front-shared';
import Checked from '@engineering/icons/checked';
import ArrowDown from '@engineering/icons/arrow-down';
import ArrowUp from '@engineering/icons/arrow-up';
import { TooltipBadge } from '@silvertours/front-legacy-entities';

const Card = styled(Ui.Card)`
  padding: 2rem 1rem 1.5rem;
  position: relative;
`;

const SuccessHeading = styled(Heading3)`
  padding-bottom: 0;
  padding-top: 0;
  margin: 1rem 0;
  ${({ theme }) => theme.mq('xl')} {
    margin: 1.5rem 0;
  }
`;

const CardWrapper = styled.div`
  margin-top: 1rem;
  ${({ theme }) => theme.mq('md')} {
    margin-top: 4rem;
  }
  ${({ theme }) => theme.mq('lg')} {
    margin-top: -2rem;
  }
  ${({ theme }) => theme.mq('xl')} {
    margin-top: -2rem;
  }
`;

const Container = styled.div`
  margin: 0 auto;
  padding: 1.5rem 2rem;
  border-radius: ${({ theme }) => theme.radius.md};
  width: 100%;
  background-color: ${({ theme }) => theme.colors.neutral.white100};
`;

const SuccessContainer = styled.div`
  text-align: center;
  font-size: 1rem;
`;

const SuccessText = styled.div`
  font-weight: bold;
  margin-bottom: 1rem;
`;

const Headline = styled.div`
  margin: 0 0 1rem;
  font-weight: bold;
  font-size: 1rem;
`;

const LegalText = styled.p<{ $textVisible: boolean; $reduceMargin: boolean }>`
  position: relative;
  margin: ${({ $reduceMargin }) => ($reduceMargin ? '1rem 0 0' : '1rem 0')};
  color: ${({ theme }) => theme.colors.neutral.grey140};
  font-size: 0.875rem;
  line-height: 1.5;
  max-height: ${({ $textVisible }) =>
    $textVisible ? '' : 'calc(3 * 1.5 * 0.875rem)'};
  max-height: ${({ $textVisible }) => ($textVisible ? '' : '3lh')};
  overflow: hidden;

  ${({ $textVisible }) =>
    !$textVisible &&
    css`
      &::after {
        position: absolute;
        left: 0;
        bottom: 0;
        height: 100%;
        width: 100%;
        content: '';
        background: linear-gradient(
          to top,
          rgb(255 255 255 / 100%) 1%,
          rgb(255 255 255 / 0%) 50%
        );
        pointer-events: none;
      }
    `};

  a {
    color: ${({ theme }) => theme.colors.brand.green140};
    text-decoration: underline;
    text-underline-offset: 0.125rem;
    transition: text-underline-offset 0.2s linear;

    &:hover {
      color: ${({ theme }) => theme.colors.brand.green160};
      text-decoration: none;
    }

    &:focus-visible {
      border-radius: ${({ theme }) => theme.radius.xs};
      outline: 0.125rem dashed ${({ theme }) => theme.colors.brand.orange100};
      outline-offset: ${({ theme }) => theme.spacing[10]};
      text-decoration: none;
    }

    &:active {
      color: ${({ theme }) => theme.colors.brand.green160};
      text-decoration: none;
      text-underline-offset: 0.25rem;
    }
  }
`;

const Form = styled.form`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

/**
 * Honeypot wrapper. Deliberately not named "honeypot" because Emotion embeds
 * the component name in the generated class name, which bots could detect.
 * Visually hidden without `display: none` so naive bots still "see" the field.
 */
const ExtraField = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

const NewsletterPictogramWrapper = styled.div`
  margin-bottom: -0.9rem;
  margin-left: 1.5rem;
`;

const NewsletterContainer = styled.article`
  position: relative;
  display: grid;
  padding: ${({ theme }) => theme.spacing[50]} 0;
  ${({ theme }) => theme.mq('md')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${({ theme }) => theme.mq('xl')} {
    margin-bottom: -1rem;
  }

  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[50]};
`;

const BadgeWrapper = styled.div`
  position: relative;
  font-size: 1rem;
  right: 0;
  top: -1.5rem;
  ${({ theme }) => theme.mq('md')} {
    right: 1.5rem;
    top: -3.725rem;
  }
`;

const StyledTooltipBadge = styled(TooltipBadge)`
  background-color: ${({ theme }) => theme.colors.brand.green60};
  background-image: unset;
`;

const NewsletterText = styled.div`
  color: ${({ theme }) => theme.colors.neutral.grey160};
  font-family: ${({ theme }) => theme.fontFamily.alternate};
`;

const List = styled.ul`
  color: ${({ theme }) => theme.colors.neutral.grey160};
`;

const ListItem = styled.li`
  font-size: 1.063rem;
  font-weight: 400;
  display: flex;
  align-items: center;
  margin: 1.6rem 0;
`;

const StyledChecked = styled(Checked)`
  fill: ${({ theme }) => theme.colors.neutral.grey160};
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  padding-right: 0.5rem;
`;

const StyledArrowDown = styled(ArrowDown)`
  fill: ${({ theme }) => theme.colors.brand.green120};
  width: 1rem;
  height: 1rem;
`;

const StyledArrowUp = styled(ArrowUp)`
  fill: ${({ theme }) => theme.colors.brand.green120};
  width: 1rem;
  height: 1rem;
`;

const ToggleLink = styled(Ui.TextLink)`
  margin-block: 0.375rem ${({ theme }) => theme.spacing[40]};
  font-size: 1.125rem;
  text-decoration: none;
`;

export {
  Container,
  SuccessContainer,
  ExtraField,
  Form,
  Headline,
  LegalText,
  Card,
  NewsletterPictogramWrapper,
  NewsletterContainer,
  BadgeWrapper,
  StyledTooltipBadge,
  NewsletterText,
  List,
  ListItem,
  StyledChecked,
  ToggleLink,
  StyledArrowDown,
  StyledArrowUp,
  CardWrapper,
  SuccessText,
  SuccessHeading,
};
